"use client";

import Link from "next/link";
import Image from "next/image";
import { useEffect } from "react";
import { motion } from "framer-motion";
import {
  Heart,
  Smartphone,
  Music,
  QrCode,
  Lock,
  FileImage,
  Star,
  ArrowRight,
  Sparkles,
  PlayCircle,
} from "lucide-react";
import { useAuthStore } from "@/store";
import { resolveCriarHref } from "@/lib/draftRoute";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { TestimonialCard } from "@/components/TestimonialCard";
import { FAQAccordion } from "@/components/FAQAccordion";
import { PricingCard } from "@/components/PricingCard";
import { SpotifyLovePage } from "@/components/wrapped/SpotifyLovePage";
import { Plan } from "@/types";
import { useRouter } from "next/navigation";

// Dados do exemplo "Ana & Lucas" usados no celular Spotify da landing.
// Espelha o mock em lib/api.ts (gift ana-e-lucas-3-anos) pra manter a mesma
// história entre a landing e o "Ver exemplo ao vivo".
const EXAMPLE_SPOTIFY_DATA = {
  partnerName: "Ana",
  recipientName: "Lucas",
  title: "Nosso Amor em Cada Detalhe",
  coupleStartDate: "2023-02-14",
  loveLetter:
    "Meu amor, cada dia ao seu lado é um presente que carrego com gratidão no coração. Obrigada por me escolher todos os dias.",
  musicName: "Nossa música",
  artistName: "Ana & Lucas",
  musicUrl: "https://open.spotify.com/track/0tgVpDi06FyKpA1z0VMD4v",
  photoUrls: [
    "https://images.unsplash.com/photo-1516589178581-6cd7833ae3b2?w=800",
  ],
  templateSlug: "purple-romantic",
};

const TESTIMONIALS = [
  {
    name: "Larissa M.",
    location: "São Paulo, SP",
    text: "Meu namorado ficou sem palavras quando abriu a ILovePage. A conta dos nossos 2 anos juntos, as fotos e a música preferida — parecia mágica. Valeu cada centavo!",
    avatar: "LM",
    rating: 5,
    occasion: "2 anos de namoro",
  },
  {
    name: "Carlos F.",
    location: "Belo Horizonte, MG",
    text: "Fiz para o aniversário da minha esposa e ela chorou de emoção. A carta com IA ficou perfeita, eu não sei escrever bem mas o resultado foi lindo.",
    avatar: "CF",
    rating: 5,
    occasion: "Aniversário",
  },
  {
    name: "Juliana R.",
    location: "Recife, PE",
    text: "Super fácil de criar! Em menos de 15 minutos tinha minha ILovePage pronta. Enviei pelo WhatsApp e meu amor adorou a surpresa no Dia dos Namorados.",
    avatar: "JR",
    rating: 5,
    occasion: "Dia dos Namorados",
  },
  {
    name: "Rafael S.",
    location: "Curitiba, PR",
    text: "Impressionante a qualidade. O QR Code impresso na cartinha que dei de presente foi um detalhe que ela vai lembrar para sempre.",
    avatar: "RS",
    rating: 5,
    occasion: "Pedido de namoro",
  },
  {
    name: "Beatriz A.",
    location: "Rio de Janeiro, RJ",
    text: "Recomendo demais! Fiz para minha melhor amiga no aniversário dela. Não precisa ser romântico para ser especial. Ela amou cada detalhe.",
    avatar: "BA",
    rating: 5,
    occasion: "Aniversário de amizade",
  },
  {
    name: "Thiago N.",
    location: "Florianópolis, SC",
    text: "Parece uma página profissional! Minha namorada pensou que eu tinha contratado um designer. O template Jardim de Rosas ficou deslumbrante.",
    avatar: "TN",
    rating: 5,
    occasion: "1 ano juntos",
  },
];

const FAQ_ITEMS = [
  {
    question: "O que é a ILovePage?",
    answer:
      "A ILovePage é uma página digital personalizada que você cria para surpreender quem ama. Ela reúne fotos do casal, uma carta de amor, a música preferida de vocês, um contador em tempo real desde o início do relacionamento e um QR Code para compartilhar pelo WhatsApp.",
  },
  {
    question: "Como funciona o pagamento?",
    answer:
      "O pagamento é feito via Pix, Cartão de Crédito ou Boleto. Após a confirmação, sua ILovePage fica disponível imediatamente com o link e QR Code para compartilhar.",
  },
  {
    question: "Posso editar depois de publicar?",
    answer:
      "Sim! Você pode editar sua ILovePage quantas vezes quiser após a publicação, sem custo adicional.",
  },
  {
    question: "A página fica disponível para sempre?",
    answer:
      "Sim — a sua ILovePage fica disponível enquanto sua conta existir, sem prazo pra acabar.",
  },
  {
    question: "Posso proteger com senha?",
    answer:
      "Sim! Você pode ativar uma senha para que apenas a pessoa amada consiga acessar. Perfeito para surpresas.",
  },
  {
    question: "Posso usar para além de namoro?",
    answer:
      "Claro! A ILovePage funciona para qualquer celebração especial: aniversários, casamentos, pedidos de namoro, declarações de amizade, homenagens aos filhos e muito mais.",
  },
  {
    question: "Precisa instalar algum aplicativo?",
    answer:
      "Não! A ILovePage funciona diretamente no navegador do celular ou computador. Basta clicar no link ou escanear o QR Code.",
  },
];

const FEATURES = [
  {
    icon: <Heart className="h-6 w-6 text-[#E11D48]" />,
    title: "Carta de amor personalizada",
    desc: "Escreva do seu jeito ou deixe a IA criar uma carta emocionante para você.",
  },
  {
    icon: <Smartphone className="h-6 w-6 text-[#E11D48]" />,
    title: "Contador ao vivo",
    desc: "Mostra anos, meses, dias, horas e segundos juntos em tempo real.",
  },
  {
    icon: <FileImage className="h-6 w-6 text-[#E11D48]" />,
    title: "Galeria de fotos",
    desc: "Adicione até 10 fotos das memórias mais especiais do casal.",
  },
  {
    icon: <Music className="h-6 w-6 text-[#E11D48]" />,
    title: "Música do casal",
    desc: "Vincule a música que é de vocês dois pelo link do Spotify.",
  },
  {
    icon: <QrCode className="h-6 w-6 text-[#E11D48]" />,
    title: "QR Code para imprimir",
    desc: "Cole no cartão, embrulho ou carta física. Ela escaneia e se surpreende.",
  },
  {
    icon: <Lock className="h-6 w-6 text-[#E11D48]" />,
    title: "Acesso por senha",
    desc: "Proteja com a data especial do casal. Só quem sabe, acessa.",
  },
];

const PLANS = [
  {
    plan: Plan.PREMIUM,
    name: "Para Sempre",
    price: 19.9,
    description: "Sua página romântica sem prazo para expirar.",
    highlight: true,
    features: [
      { text: "Página personalizada", included: true },
      { text: "Mensagem especial", included: true },
      { text: "Fotos do casal", included: true },
      { text: "Link permanente compartilhável", included: true },
      { text: "QR Code compartilhável", included: true },
      { text: "Página sem expiração", included: true },
    ],
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0 },
};

export default function HomePage() {
  const router = useRouter();
  // Usuário logado não tem nada a fazer na landing — vai direto pro
  // painel. Esperamos a hidratação do auth pra evitar flicker de
  // redirect quando o token ainda nem foi lido do localStorage.
  // Usa replace pra não criar entrada no histórico.
  const user = useAuthStore((s) => s.user);
  const hasHydrated = useAuthStore((s) => s.hasHydrated);

  useEffect(() => {
    if (hasHydrated && user) {
      router.replace("/dashboard");
    }
  }, [hasHydrated, user, router]);

  const handleSelectPlan = (plan: Plan) => {
    // Navega direto pra /criar/<id> resolvendo o draftId no clique.
    // Antes íamos a /criar (sem ID) que mostrava uma tela intermediária
    // de redirecionamento — agora pulamos essa etapa.
    router.push(resolveCriarHref(`plan=${plan}`));
  };

  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#FFF8FA] via-[#FFE4E6] to-[#FFF7ED] px-4 py-14 text-center sm:py-24 lg:py-32">
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute text-rose-200"
              style={{
                left: `${10 + i * 12}%`,
                top: `${15 + (i % 3) * 25}%`,
                fontSize: `${16 + (i % 3) * 8}px`,
              }}
              animate={{ y: [0, -12, 0], opacity: [0.3, 0.7, 0.3] }}
              transition={{ duration: 3 + i * 0.5, repeat: Infinity, delay: i * 0.3 }}
            >
              ♥
            </motion.div>
          ))}
        </div>

        <div className="relative mx-auto max-w-4xl">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <Badge variant="default" className="mb-6 text-xs px-4 py-1.5">
              <Sparkles className="h-3 w-3 mr-1.5" />
              Mais de 10.000 casais surpreendidos
            </Badge>
          </motion.div>

          <motion.h1
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-[clamp(1.875rem,7vw,3.75rem)] font-black leading-[1.1] tracking-tight text-[#1F2937] sm:text-6xl"
          >
            Transforme sua{" "}
            <span className="text-[#E11D48]">história de amor</span>
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            em uma página inesquecível
          </motion.h1>

          <motion.p
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mx-auto mt-5 sm:mt-6 max-w-2xl text-base sm:text-lg text-gray-600 leading-relaxed"
          >
            Crie uma surpresa romântica com fotos, música, carta de amor, contador do casal e QR
            Code. Tudo pronto para enviar pelo WhatsApp em poucos minutos.
          </motion.p>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-8 flex flex-col items-center gap-3 sm:mt-10 sm:flex-row sm:justify-center sm:gap-4"
          >
            <Button size="xl" asChild className="w-full sm:w-auto shadow-xl shadow-rose-300">
              <Link href="/criar">
                <Heart className="h-5 w-5 fill-white" />
                Criar minha ILovePage
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Button>
            <Button size="xl" variant="outline" asChild className="w-full sm:w-auto">
              <Link href="/p/jp-e-para-ela-1779550799053">
                <PlayCircle className="h-5 w-5" />
                Ver a página que vai emocionar ela
              </Link>
            </Button>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-gray-500"
          >
            <div className="flex items-center gap-1">
              <Star className="h-4 w-4 text-[#F59E0B] fill-[#F59E0B]" />
              <span className="font-semibold text-[#1F2937]">4.9</span>
              <span>de 5</span>
            </div>
            <div className="hidden sm:block h-4 w-px bg-gray-200" />
            <span>+10.000 ILovePages criadas</span>
            <div className="hidden sm:block h-4 w-px bg-gray-200" />
            <span>Pronto em 15 minutos</span>
            <div className="hidden sm:block h-4 w-px bg-gray-200" />
            <Link
              href="#precos"
              className="font-semibold text-[#E11D48] hover:underline"
            >
              Ver planos
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Preview mockup */}
      <section className="py-12 sm:py-16 px-4 bg-white">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.6 }}
            >
              <Badge variant="default" className="mb-4">Como funciona</Badge>
              <h2 className="text-3xl font-black text-[#881337] leading-tight mb-4">
                Em 3 passos simples, sua surpresa fica pronta
              </h2>
              <div className="relative flex flex-col gap-6 mt-8">
                {/* Linha conectando os 3 passos — guia visual de progressão.
                    Posicionada atrás dos círculos (z-0) na coluna esquerda. */}
                <div className="absolute left-5 top-5 bottom-5 w-px bg-gradient-to-b from-[#E11D48] via-[#FFE4E6] to-transparent" aria-hidden />
                {[
                  { n: 1, title: "Personalize sua história", desc: "Adicione fotos, escreva a carta, coloque a música e escolha o tema." },
                  { n: 2, title: "Publique com segurança", desc: "Escolha o plano, faça o pagamento via Pix e sua página fica online na hora." },
                  { n: 3, title: "Surpreenda pelo WhatsApp", desc: "Envie o link ou imprima o QR Code. A reação vai derreter qualquer coração." },
                ].map(({ n, title, desc }) => (
                  <div key={n} className="relative flex items-start gap-4">
                    <div className="relative z-10 shrink-0 h-10 w-10 rounded-full bg-gradient-to-br from-[#E11D48] to-[#881337] flex items-center justify-center text-white font-black text-lg shadow-[0_6px_18px_rgba(225,29,72,0.30)] ring-4 ring-white">
                      {n}
                    </div>
                    <div className="pt-1">
                      <h3 className="font-bold text-[#1F2937]">{title}</h3>
                      <p className="text-sm text-gray-500 mt-0.5">{desc}</p>
                    </div>
                  </div>
                ))}
              </div>
              <Button size="lg" className="mt-8" asChild>
                <Link href="/criar">
                  Começar agora
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="flex justify-center"
            >
              <div className="relative">
                {/* Blur decorativo rosa/laranja — mantém a identidade da landing
                    contrastando com o celular escuro estilo Spotify. */}
                <div className="absolute -inset-4 bg-gradient-to-br from-rose-200 to-orange-100 rounded-3xl blur-2xl opacity-40" />

                {/* Moldura de celular escura (mesma estética do preview do wizard) */}
                <div
                  className="relative w-[280px] h-[560px] rounded-[36px] overflow-hidden shadow-2xl"
                  style={{
                    border: "8px solid #1a1a2e",
                    background: "#121212",
                  }}
                >
                  {/* Notch decorativo */}
                  <div className="absolute top-0 inset-x-0 z-20 flex justify-center pt-1 pointer-events-none">
                    <div
                      className="rounded-b-xl flex items-center gap-1.5 px-6 py-1"
                      style={{ background: "#0F0520" }}
                    >
                      <div className="w-8 h-1 rounded-full bg-white/10" />
                      <div className="w-1.5 h-1.5 rounded-full bg-purple-400/60" />
                    </div>
                  </div>

                  {/* Conteúdo Spotify — rola dentro da moldura */}
                  <div
                    className="absolute inset-0 pt-7 overflow-y-auto overflow-x-hidden"
                    style={{ scrollbarWidth: "none" }}
                  >
                    <SpotifyLovePage data={EXAMPLE_SPOTIFY_DATA} />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-14 sm:py-20 px-4 bg-[#FFF8FA]">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <Badge variant="default" className="mb-4">Recursos</Badge>
            <h2 className="text-3xl font-black text-[#881337]">Tudo para criar a surpresa perfeita</h2>

            <p className="text-gray-500 mt-2 max-w-lg mx-auto">
              Cada detalhe pensado para fazer o coração dela acelerar.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {FEATURES.map((f, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ delay: i * 0.08 }}
                className="group flex gap-4 p-6 bg-white rounded-2xl border border-rose-100 hover:border-rose-200 hover:-translate-y-0.5 hover:shadow-[0_12px_28px_rgba(225,29,72,0.10)] transition-all duration-200"
              >
                <div className="shrink-0 h-12 w-12 rounded-xl bg-rose-50 flex items-center justify-center group-hover:bg-rose-100 transition-colors">
                  {f.icon}
                </div>
                <div>
                  <h3 className="font-bold text-[#1F2937]">{f.title}</h3>
                  <p className="text-sm text-gray-500 mt-1">{f.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-14 sm:py-20 px-4 bg-white" id="precos">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <Badge variant="default" className="mb-4">Planos</Badge>
            <h2 className="text-3xl font-black text-[#881337]">
              Escolha o plano ideal para sua surpresa
            </h2>
            <p className="text-gray-500 mt-2">Pagamento único, sem assinatura.</p>
          </motion.div>

          <div className="mx-auto grid max-w-3xl grid-cols-1 gap-6 sm:grid-cols-2 items-start">
            {PLANS.map((p) => (
              <motion.div
                key={p.plan}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
              >
                <PricingCard {...p} onSelect={handleSelectPlan} />
              </motion.div>
            ))}
          </div>
          <p className="mx-auto mt-6 max-w-md text-center text-sm text-gray-500">
            Por apenas R$10 a mais, sua página fica disponível{" "}
            <span className="font-semibold text-[#E11D48]">para sempre</span>.
          </p>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-14 sm:py-20 px-4 bg-[#FFF8FA]">
        <div className="mx-auto max-w-6xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-14"
          >
            <Badge variant="default" className="mb-4">Depoimentos</Badge>
            <h2 className="text-3xl font-black text-[#881337]">
              O que dizem quem já surpreendeu
            </h2>
            <div className="flex items-center justify-center gap-2 mt-2">
              {Array.from({ length: 5 }).map((_, i) => (
                <Star key={i} className="h-5 w-5 text-[#F59E0B] fill-[#F59E0B]" />
              ))}
              <span className="text-sm text-gray-500 ml-1">4.9 de 5 — +1.200 avaliações</span>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {TESTIMONIALS.map((t, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ delay: i * 0.07 }}
              >
                <TestimonialCard {...t} />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 sm:py-20 px-4 bg-white">
        <div className="mx-auto max-w-3xl">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-12"
          >
            <Badge variant="default" className="mb-4">Perguntas frequentes</Badge>
            <h2 className="text-3xl font-black text-[#881337]">Ficou alguma dúvida?</h2>
          </motion.div>
          <FAQAccordion items={FAQ_ITEMS} />
        </div>
      </section>

      {/* CTA Final */}
      <section className="py-16 sm:py-24 px-4 bg-gradient-to-br from-[#E11D48] to-[#881337] text-white text-center">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeUp}
          className="mx-auto max-w-2xl"
        >
          <Heart className="h-10 sm:h-12 w-10 sm:w-12 fill-white mx-auto mb-5 sm:mb-6 opacity-80" />
          <h2 className="text-3xl sm:text-4xl font-black leading-tight mb-4">
            Ela merece ser surpreendida
          </h2>
          <p className="text-rose-100 text-base sm:text-lg mb-8 sm:mb-10 leading-relaxed">
            Crie agora e deixe ela sem palavras. É rápido, bonito e vai ficar guardado no coração dela para sempre.
          </p>
          <Button size="xl" variant="gold" asChild className="w-full sm:w-auto shadow-xl">
            <Link href="/criar">
              <Heart className="h-5 w-5 fill-white" />
              Criar minha ILovePage agora
              <ArrowRight className="h-5 w-5" />
            </Link>
          </Button>
          <p className="mt-4 text-rose-200 text-sm">
            Pronto em menos de 15 minutos · Pagamento 100% seguro
          </p>
        </motion.div>
      </section>

      {/* Footer — fundo claro pra logo PNG ficar "sem fundo" mesmo,
          aparecendo só o desenho sobre o creme da identidade. */}
      <footer className="bg-[#FFF8FA] text-gray-500 pt-14 px-4 pb-[max(2rem,env(safe-area-inset-bottom))] text-center text-sm border-t border-rose-100">
        <div className="flex items-center justify-center mb-5">
          <Image
            src="/brand/logo-horizontal.png"
            alt="ILovePage"
            width={2172}
            height={724}
            className="h-16 sm:h-20 w-auto"
          />
        </div>
        <p className="text-gray-500">© 2026 ILovePage · Feito com amor no Brasil 🇧🇷</p>
        <div className="flex flex-wrap items-center justify-center gap-x-5 gap-y-2 mt-4 text-xs">
          <Link href="/termos" className="text-gray-500 hover:text-[#E11D48] transition-colors py-1">Termos de uso</Link>
          <span className="text-rose-200">·</span>
          <Link href="/privacidade" className="text-gray-500 hover:text-[#E11D48] transition-colors py-1">Privacidade</Link>
          <span className="text-rose-200">·</span>
          <Link href="/contato" className="text-gray-500 hover:text-[#E11D48] transition-colors py-1">Contato</Link>
        </div>
      </footer>
    </div>
  );
}
